import { withRef } from 'hoc';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router';
import { Ref, type JSX, useEffect } from 'react';
import { Box, Alert, Button } from '@mui/material';
import { PageContainer, useActivePage } from '@toolpad/core';

import { Loader } from 'components/loader';
import { Messages, AppRoutes } from 'config';
import { CrudMode, ChildrenProps } from 'types';

import { DetailsProps } from './types';

const Details = ({
  id,
  mode,
  error,
  title,
  isError,
  refetch,
  children,
  isLoading,
  refForward,
  className = '',
  childrenClassName,
  loaderAbsolutePosition,
  retryBtnText = 'Retry',
  loaderFullScreen = false,
  notFoundPath = AppRoutes.NotFound,
}: ChildrenProps<DetailsProps>): JSX.Element => {
  const hasChildren = !!children;
  const navigate = useNavigate();
  const activePage = useActivePage();

  const errorMessage = error || Messages.SOMETHING_WENT_WRONG;

  useEffect(() => {
    if (!isLoading && !isError && !hasChildren) {
      navigate(notFoundPath);
    }
  }, [isError, isLoading, hasChildren, notFoundPath, navigate]);

  const getMainElement = () => {
    if (isLoading) {
      return (
        <Loader
          isLoading
          isFullScreen={loaderFullScreen}
          absolutePosition={loaderAbsolutePosition}
        />
      );
    }

    if (isError) {
      const errorContent = (
        <Box
          className={twMerge(
            'flex flex-col gap-2',
            !!title && !!mode && 'w-full'
          )}
        >
          <Alert severity="error">{errorMessage}</Alert>
          {refetch && <Button onClick={refetch}>{retryBtnText}</Button>}
        </Box>
      );

      if (title && mode !== undefined) {
        return (
          <PageContainer
            title={title}
            maxWidth={false}
            breadcrumbs={
              activePage && mode !== CrudMode.LIST
                ? [
                    ...activePage.breadcrumbs,
                    {
                      title,
                    },
                  ]
                : activePage?.breadcrumbs
            }
          >
            <Box sx={{ flexGrow: 1 }}>{errorContent}</Box>
          </PageContainer>
        );
      }

      return (
        <Box className="flex flex-1 flex-col items-center justify-center gap-2">
          {errorContent}
        </Box>
      );
    }

    return (
      <Box
        className={twMerge(
          'flex-1 overflow-x-hidden overflow-y-auto',
          childrenClassName
        )}
      >
        {children}
      </Box>
    );
  };

  return (
    <Box
      id={id}
      ref={refForward as Ref<HTMLDivElement>}
      className={twMerge(
        'flex h-full min-h-0 w-full flex-col',
        isLoading && 'h-screen',
        className
      )}
    >
      {getMainElement()}
    </Box>
  );
};

export const DetailsWithRef = withRef(Details);

export { Details };
